import apiClient from './apiClient';
import type { Album, AlbumCustomValue, Media, Uuid } from '../types';

const bffApiUrl = '';
const apiUrl = '/direct/picsvc';

export interface AlbumAssocPayload {
  albumCode: string;
}

export interface AlbumUpdatePayload {
  title?: string;
  published?: boolean;
  access_code?: string;
  access_type?: string;
  parent_id?: Uuid | null;
  tags?: Record<string, string>;
  album_type_id?: Uuid | null;
  /** Partial: a key set to null removes that value. */
  custom_values?: Record<Uuid, AlbumCustomValue | null>;
}

export interface AlbumAssocResponse {
  album_id?: Uuid;
  [key: string]: unknown;
}

export interface AlbumCreatePayload {
  title: string;
  parent_id?: Uuid | null;
  tags?: Record<string, string>;
  album_type_id?: Uuid | null;
}

export interface AlbumImportNode {
  title: string;
  tags: Record<string, string>;
  depth: number;
  status: 'create' | 'duplicate' | 'error';
  error: string | null;
  warning: string | null;
  children: AlbumImportNode[];
}

export interface AlbumImportPlan {
  valid: boolean;
  executed: boolean;
  counts: { create: number; errors: number; warnings: number };
  nodes: AlbumImportNode[];
}

export interface AlbumImportPayload {
  csv: string;
  parent_id?: Uuid | null;
}

export interface FinalizeUploadPayload {
  blob_signed_id: string;
  image_hash?: string;
  album_id?: Uuid | null;
}

export interface MediaLifecycleResult {
  ok: Uuid[];
  skipped: { id: Uuid; reason: 'referenced' | 'not_found' }[];
}

export interface MoveAlbumPicsResult {
  ok: Uuid[];
  skipped: { id: Uuid; reason: string }[];
}

// The finalized media plus, when album_id was supplied, its membership.
export interface FinalizedMedia extends Media {
  album_media?: {
    id: Uuid;
    album_id: Uuid;
    user_pic_id: Uuid;
    pos: number;
    assignment_type: 'absolute' | 'transitive';
  };
}

export const ALBUM_SEARCH_MIN_LENGTH = 2;
export const ALBUM_SEARCH_TAG_MIN_LENGTH = 3;
export const ALBUM_SEARCH_MAX_LENGTH = 100;

const picApi = {
  albumAssoc: ({ albumCode }: AlbumAssocPayload) =>
    apiClient.post<AlbumAssocResponse>(`${bffApiUrl}/albums/assoc`, {
      access_code: albumCode,
    }),

  // Removes the caller's own assoc (and their notifications on that album).
  closeAlbum: (albumId: Uuid) => apiClient.delete(`${apiUrl}/album_assocs/${albumId}`),

  getAlbumList: () => apiClient.get<Album[]>(`${apiUrl}/albums/associated`),

  getAlbum: (albumId: Uuid) => apiClient.get<Album>(`${apiUrl}/albums/${albumId}`),

  getPic: (albumId: Uuid, picId: Uuid) =>
    apiClient.get<Media>(`${apiUrl}/albums/${albumId}/pics/${picId}`),

  getAlbumChildren: (albumId: Uuid) =>
    apiClient.get<Album[]>(`${apiUrl}/albums/${albumId}/children`),

  getAlbumPath: (albumId: Uuid) =>
    apiClient.get<Uuid[]>(`${apiUrl}/albums/${albumId}/path`),

  getAllAlbums: () => apiClient.get<Album[]>(`${apiUrl}/albums`),

  createAlbum: (payload: AlbumCreatePayload) =>
    apiClient.post<Album>(`${apiUrl}/albums`, payload),

  importPreview: (payload: AlbumImportPayload) =>
    apiClient.post<AlbumImportPlan>(`${apiUrl}/album_imports/preview`, payload),

  importAlbums: (payload: AlbumImportPayload) =>
    apiClient.post<AlbumImportPlan>(`${apiUrl}/album_imports`, payload),

  finalizeUpload: (payload: FinalizeUploadPayload) =>
    apiClient.post<FinalizedMedia>(`${apiUrl}/pic_uploads`, payload),

  updateAlbum: (albumId: Uuid, patch: AlbumUpdatePayload) =>
    apiClient.patch<Album>(`${apiUrl}/albums/${albumId}`, patch),

  disableMedia: (mediaIds: Uuid[], disabled: boolean) =>
    apiClient.patch<MediaLifecycleResult>(`${apiUrl}/media/disabled`, { media_ids: mediaIds, disabled }),

  removeAlbumMedia: (albumId: Uuid, mediaIds: Uuid[]) =>
    apiClient.post<MediaLifecycleResult>(`${apiUrl}/albums/${albumId}/pics/remove`, { media_ids: mediaIds }),

  reorderAlbumPics: (albumId: Uuid, mediaIds: Uuid[]) =>
    apiClient.patch<Record<string, never>>(`${apiUrl}/albums/${albumId}/pics/reorder`, {
      media_ids: mediaIds,
    }),

  moveAlbumPics: (albumId: Uuid, mediaIds: Uuid[], targetAlbumId: Uuid) =>
    apiClient.post<MoveAlbumPicsResult>(`${apiUrl}/albums/${albumId}/pics/move`, {
      media_ids: mediaIds,
      target_album_id: targetAlbumId,
    }),
};

export default picApi;
